import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/tr';
import { AnimatePresence, animate, motion, useDragControls, useMotionValue } from 'framer-motion';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from 'src/services/redux/app/store';
import { INotificationItem, notificationsUnreadCount } from 'src/services/redux/slices/notifications/notifications-slice';
import { ChatSocketContext } from 'src/app/pages/messages/context/ChatSocketContext';
import { useFeatureTour } from 'src/app/components/feature-tour/FeatureTourProvider';
import { notificationDockTour, NOTIFICATION_DOCK_TOUR_ID } from './notificationDock.tour';
import './notification-dock.css';

/**
 * NotificationDock — zil bildirimleri için kenara yapışan sürüklenebilir düğme +
 * alttan kayarak açılan (bottom sheet) bildirim paneli.
 *
 * - Düğme ekranın sol/sağ kenarına yapışır; dikeyde sürüklenebilir, karşı kenara
 *   atılırsa oraya geçer. Konum localStorage'da saklanır.
 * - Panel: düğme hangi kenardaysa o kenardan kayarak açılan çekmece (sağ → sağdan,
 *   sol → soldan). İki kademe genişlik (dar / tam ekrana yakın): tutamacı ekranın
 *   ortasına doğru kaydırma veya tutamaca tıklama büyütür, kenara doğru çekme önce
 *   küçültür sonra kapatır. Filtre çipleri,
 *   okundu/okunmadı değiştirme, tümünü okundu işaretleme, sayfalı yükleme.
 * - Canlı: backend `notification:new` (utils/notifications.js) olayını dinler.
 * (7 Eki 2026 — zil bildirimleri üst çubuktan kaldırılmıştı, CRM'de görünmüyordu.)
 */

const API = `${process.env.REACT_APP_API_URL}`;
const STORAGE_KEY = 'aycrm.notificationDock';
const PAGE_SIZE = 20;
const HANDLE_W = 46;
const HANDLE_H = 92;
const SHEET_HALF_W = 520; // açılış genişliği (px, pencereden genişse pencere)
const SHEET_FULL_RATIO = 0.96; // büyütülmüş: pencerenin %96'sı

type Side = 'left' | 'right';
type Filter = 'all' | 'unread' | 'personal' | 'general';

interface DockPos {
  side: Side;
  top: number; // px, pencere üstünden
}

const FILTERS: Array<{ key: Filter; label: string }> = [
  { key: 'all', label: 'Tümü' },
  { key: 'unread', label: 'Okunmamış' },
  { key: 'personal', label: 'Bana özel' },
  { key: 'general', label: 'Genel' },
];

// Projede Bootstrap Icons fontu yüklü değil; simgeler satır içi SVG
const Svg: React.FC<{ d: string; size?: number; className?: string; fill?: boolean }> = ({ d, size = 16, className, fill }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={fill ? 'currentColor' : 'none'}
    stroke={fill ? 'none' : 'currentColor'}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d={d} />
  </svg>
);
const ICON = {
  bell: 'M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0',
  bellOff: 'M13.73 21a2 2 0 0 1-3.46 0M18.63 13A17.9 17.9 0 0 1 18 8M6.26 6.26A5.86 5.86 0 0 0 6 8c0 7-3 9-3 9h14M18 8a6 6 0 0 0-9.33-5M1 1l22 22',
  checkAll: 'M18 6 7 17l-5-5M22 10l-7.5 7.5',
  close: 'M18 6 6 18M6 6l12 12',
  mail: 'M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM22 6l-10 7L2 6',
  mailOpen: 'M22 10.5V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-9.5a2 2 0 0 1 1-1.73l8-4.6a2 2 0 0 1 2 0l8 4.6a2 2 0 0 1 1 1.73zM22 10.5l-10 6.5-10-6.5',
  more: 'M12 5v14M5 12l7 7 7-7',
  chevronLeft: 'M15 18l-6-6 6-6',
  chevronRight: 'M9 18l6-6-6-6',
  help: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3M12 17h.01',
};

const clampTop = (top: number) => {
  const max = Math.max(80, (typeof window !== 'undefined' ? window.innerHeight : 800) - HANDLE_H - 24);
  return Math.min(Math.max(72, top), max);
};

const loadPos = (): DockPos => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      if ((p.side === 'left' || p.side === 'right') && typeof p.top === 'number') return { side: p.side, top: clampTop(p.top) };
    }
  } catch {
    /* localStorage kapalı olabilir */
  }
  return { side: 'right', top: clampTop(Math.round((typeof window !== 'undefined' ? window.innerHeight : 800) * 0.42)) };
};

const savePos = (p: DockPos) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    /* sessiz */
  }
};

const NotificationDock: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const unreadCount = useAppSelector((state) => state?.notifications?.unreadCount || 0);
  const socketCtx = useContext(ChatSocketContext);
  const socket = socketCtx?.socket || null;
  const tour = useFeatureTour();
  const tourActive = tour.activeTourId === NOTIFICATION_DOCK_TOUR_ID;

  // ── Düğme konumu ──
  const [pos, setPos] = useState<DockPos>(loadPos);
  const dragX = useMotionValue(0);
  const dragY = useMotionValue(0);
  const handleRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);

  // ── Panel ──
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const sheetDrag = useDragControls();
  const [vw, setVw] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1280));
  const [filter, setFilter] = useState<Filter>('all');
  const [items, setItems] = useState<INotificationItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busyIds, setBusyIds] = useState<Set<number>>(new Set());
  const [shake, setShake] = useState(0);

  const refreshCount = useCallback(() => {
    dispatch(notificationsUnreadCount());
  }, [dispatch]);

  const fetchPage = useCallback(async (targetPage: number, targetFilter: Filter, replace: boolean) => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page: targetPage, limit: PAGE_SIZE, sortBy: 'desc' };
      if (targetFilter === 'unread') params.only_unread = 1;
      if (targetFilter === 'personal') params.scope = 'personal';
      if (targetFilter === 'general') params.scope = 'general';
      const res = await axios.get(`${API}/notifications/my-list`, { params });
      const results: INotificationItem[] = res?.data?.results || [];
      setItems((prev) => (replace ? results : [...prev, ...results.filter((r) => !prev.some((p) => p.id === r.id))]));
      setPage(res?.data?.page || targetPage);
      setTotalPages(res?.data?.totalPages || 1);
      setTotalResults(res?.data?.totalResults || results.length);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Bildirimler yüklenemedi', { autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  }, []);

  // Açılışta ve filtre değişiminde ilk sayfa
  useEffect(() => {
    if (!open) return;
    fetchPage(1, filter, true);
    refreshCount();
  }, [open, filter, fetchPage, refreshCount]);

  // Rozet: ilk yükleme + 2 dk'da bir emniyet yenilemesi (socket kaçarsa)
  useEffect(() => {
    refreshCount();
    const t = setInterval(refreshCount, 120000);
    return () => clearInterval(t);
  }, [refreshCount]);

  // Canlı bildirim
  useEffect(() => {
    if (!socket) return;
    const onNew = (n: INotificationItem) => {
      if (!n || !n.id) return;
      refreshCount();
      setShake((s) => s + 1);
      toast.info(
        <div>
          <div className="fw-bold">{n.title}</div>
          {n.content && <div className="fs-8 text-gray-700 mt-1">{String(n.content).slice(0, 140)}</div>}
        </div>,
        { autoClose: 7000 },
      );
      setItems((prev) => {
        if (prev.some((p) => p.id === n.id)) return prev;
        const matches = filter === 'all' || (filter === 'unread' && !n.is_read) || (filter === 'personal' && n.user_id != null) || (filter === 'general' && n.user_id == null);
        return matches ? [n, ...prev] : prev;
      });
      setTotalResults((t) => t + 1);
    };
    socket.on('notification:new', onNew);
    return () => {
      socket.off('notification:new', onNew);
    };
  }, [socket, filter, refreshCount]);

  // Pencere boyu değişince düğme ekran dışında kalmasın
  useEffect(() => {
    const onResize = () => {
      setPos((p) => ({ ...p, top: clampTop(p.top) }));
      setVw(window.innerWidth);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Rehber (walkthrough): tur tanımı + turun kullanacağı eylemler
  useEffect(() => tour.registerTour(notificationDockTour), [tour.registerTour]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(
    () =>
      tour.registerActions(NOTIFICATION_DOCK_TOUR_ID, {
        open: () => setOpen(true),
        close: () => setOpen(false),
        expand: () => setExpanded(true),
        collapse: () => setExpanded(false),
        setFilter: (f: Filter) => setFilter(f),
        // Rehber gösterimi: düğme yukarı-aşağı kayar, karşı kenara gidip geri döner
        // (konum DEĞİŞMEZ; yalnızca geçici transform). Spot ışığı düğmeyi takip eder.
        demoDrag: async () => {
          const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
          draggingRef.current = true;
          const ease = 'easeInOut' as const;
          animate(dragY, -90, { duration: 0.55, ease });
          await wait(600);
          animate(dragY, 90, { duration: 0.7, ease });
          await wait(750);
          animate(dragY, 0, { duration: 0.5, ease });
          await wait(550);
          const across = (window.innerWidth - HANDLE_W) * (handleRef.current && handleRef.current.classList.contains('left') ? 1 : -1);
          animate(dragX, across, { duration: 0.9, ease });
          await wait(1500);
          animate(dragX, 0, { duration: 0.9, ease });
          await wait(950);
          draggingRef.current = false;
        },
      }),
    [tour.registerActions], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Kapanınca bir sonraki açılış yarım kademeden başlasın
  useEffect(() => {
    if (!open) setExpanded(false);
  }, [open]);

  // ESC ile kapat
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !tourActive && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, tourActive]);

  const toggleRead = async (n: INotificationItem) => {
    if (busyIds.has(n.id)) return;
    const next = !n.is_read;
    setBusyIds((s) => new Set(s).add(n.id));
    try {
      await axios.post(`${API}/notifications/read`, { id: n.id, is_read: next });
      setItems((prev) => (filter === 'unread' && next ? prev.filter((p) => p.id !== n.id) : prev.map((p) => (p.id === n.id ? { ...p, is_read: next } : p))));
      refreshCount();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Bildirim güncellenemedi', { autoClose: 3000 });
    } finally {
      setBusyIds((s) => {
        const c = new Set(s);
        c.delete(n.id);
        return c;
      });
    }
  };

  const markAllRead = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await axios.post(`${API}/notifications/read`, { is_read: true });
      setItems((prev) => (filter === 'unread' ? [] : prev.map((p) => ({ ...p, is_read: true }))));
      refreshCount();
      toast.success('Tüm bildirimler okundu olarak işaretlendi', { autoClose: 2500 });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'İşlem başarısız', { autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  };

  // ── Düğme sürükleme: bırakınca en yakın kenara yapış, dikey konumu sakla ──
  const onDragEnd = () => {
    const el = handleRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const next: DockPos = { side: centerX < window.innerWidth / 2 ? 'left' : 'right', top: clampTop(rect.top) };
      setPos(next);
      savePos(next);
    }
    dragX.set(0);
    dragY.set(0);
    // Tıklama ile karışmasın: kısa süre sonra serbest bırak
    setTimeout(() => {
      draggingRef.current = false;
    }, 50);
  };

  const badge = unreadCount > 99 ? '99+' : String(unreadCount);
  const side = pos.side;
  const halfW = Math.min(SHEET_HALF_W, vw);
  const fullW = Math.round(vw * SHEET_FULL_RATIO);
  // Kenar düğmesindeki ok: darken ekranın ortasına, genişken kenara bakar
  const edgeArrowDir: 'left' | 'right' = (side === 'right') !== expanded ? 'left' : 'right';
  const userName = useMemo(() => (user?.name ? String(user.name).split(' ')[0] : ''), [user]);

  return (
    <>
      {/* Kenar düğmesi */}
      <motion.div
        ref={handleRef}
        className={`notif-dock-handle ${pos.side} ${unreadCount > 0 ? 'has-unread' : ''}`}
        data-tour="notif-handle"
        style={{ top: pos.top, x: dragX, y: dragY, width: HANDLE_W, height: HANDLE_H }}
        drag
        dragMomentum={false}
        dragElastic={0.12}
        onDragStart={() => {
          draggingRef.current = true;
        }}
        onDragEnd={onDragEnd}
        onTap={() => {
          if (draggingRef.current) return;
          setOpen(true);
        }}
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.96 }}
        title="Bildirimler"
        role="button"
        aria-label={`Bildirimler${unreadCount ? ` (${unreadCount} okunmamış)` : ''}`}
      >
        <motion.span
          key={shake}
          className="notif-dock-bell"
          initial={{ rotate: 0 }}
          animate={shake ? { rotate: [0, -18, 16, -12, 10, -6, 0] } : { rotate: 0 }}
          transition={{ duration: 0.7 }}
        >
          <Svg d={ICON.bell} size={22} />
        </motion.span>
        {unreadCount > 0 && <span className="notif-dock-badge">{badge}</span>}
      </motion.div>

      {/* Alttan açılan panel */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="notif-sheet-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => !tourActive && setOpen(false)}
            />
            <motion.div
              className={`notif-sheet ${side}`}
              data-tour="notif-sheet"
              role="dialog"
              aria-modal="true"
              aria-label="Bildirimler"
              initial={{ x: side === 'right' ? '100%' : '-100%', width: halfW }}
              animate={{ x: 0, width: expanded ? fullW : halfW }}
              exit={{ x: side === 'right' ? '100%' : '-100%' }}
              transition={{ type: 'spring', stiffness: 380, damping: 36 }}
              drag="x"
              dragListener={false}
              dragControls={sheetDrag}
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={side === 'right' ? { left: 0.3, right: 0.55 } : { left: 0.55, right: 0.3 }}
              onDragEnd={(_, info) => {
                // Ekranın ortasına doğru: büyüt. Kenara doğru: büyükse küçült, değilse kapat.
                const toCenter = side === 'right' ? -info.offset.x : info.offset.x;
                const vCenter = side === 'right' ? -info.velocity.x : info.velocity.x;
                if (toCenter > 60 || vCenter > 500) setExpanded(true);
                else if (toCenter < -120 || vCenter < -600) {
                  if (expanded) setExpanded(false);
                  else setOpen(false);
                }
              }}
            >
              {/* İç kenardaki genişlet/daralt düğmesi: ok, panelin gideceği yöne bakar ve o yöne
              doğru hafifçe hareket eder; sürüklenebilir, tıklanınca kademe değişir */}
              <button
                type="button"
                className={`notif-sheet-edge ${expanded ? 'is-expanded' : ''}`}
                data-tour="notif-edge"
                onPointerDown={(e) => sheetDrag.start(e)}
                onClick={() => setExpanded((v) => !v)}
                title={expanded ? 'Paneli daralt' : 'Paneli tam ekrana yakın genişlet'}
                aria-label={expanded ? 'Paneli daralt' : 'Paneli genişlet'}
                aria-pressed={expanded}
              >
                <span className="notif-edge-pill" aria-hidden="true">
                  <motion.span
                    className="notif-edge-arrow"
                    animate={{ x: edgeArrowDir === 'left' ? [0, -4, 0] : [0, 4, 0] }}
                    transition={{ duration: expanded ? 2.2 : 1.3, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    <Svg d={edgeArrowDir === 'left' ? ICON.chevronLeft : ICON.chevronRight} size={15} />
                  </motion.span>
                </span>
                <span className="notif-edge-tip">{expanded ? 'Daralt' : 'Genişlet'}</span>
              </button>

              {/* Başlık alanından da sürüklenebilir; liste normal kaydırılır */}
              <div className="notif-sheet-top" onPointerDown={(e) => sheetDrag.start(e)}>
                <div className="notif-sheet-header">
                  <div className="d-flex align-items-center gap-2">
                    <span className="notif-sheet-icon">
                      <Svg d={ICON.bell} size={20} />
                    </span>
                    <div>
                      <div className="fw-bolder fs-4 text-gray-900">Bildirimler</div>
                      <div className="fs-8 text-gray-600">
                        {unreadCount > 0 ? `${unreadCount} okunmamış` : 'Hepsi okundu'}
                        {userName ? ` · ${userName}` : ''}
                      </div>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {tour.isEnabled(NOTIFICATION_DOCK_TOUR_ID) && (
                      <button
                        type="button"
                        className="btn btn-sm btn-icon btn-light notif-help-btn"
                        onClick={() => tour.startTour(NOTIFICATION_DOCK_TOUR_ID)}
                        onPointerDown={(e) => e.stopPropagation()}
                        title="Bu paneli nasıl kullanırım? (rehber)"
                        aria-label="Rehberi başlat"
                      >
                        <Svg d={ICON.help} size={16} />
                      </button>
                    )}
                    <button
                      type="button"
                      className="d-flex flex-row align-items-center btn btn-sm btn-light-primary"
                      data-tour="notif-mark-all"
                      onClick={markAllRead}
                      onPointerDown={(e) => e.stopPropagation()}
                      disabled={loading || unreadCount === 0}
                    >
                      <Svg d={ICON.checkAll} size={14} className="me-1" />
                      Tümünü okundu işaretle
                    </button>
                    <button type="button" className="btn btn-sm btn-icon btn-light" onClick={() => setOpen(false)} onPointerDown={(e) => e.stopPropagation()} aria-label="Kapat">
                      <Svg d={ICON.close} size={16} />
                    </button>
                  </div>
                </div>
              </div>

              <div className="notif-sheet-filters" data-tour="notif-filters">
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    className={`notif-chip ${filter === f.key ? 'active' : ''}`}
                    onClick={() => setFilter(f.key)}
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    {f.label}
                    {f.key === 'unread' && unreadCount > 0 && <span className="notif-chip-count">{badge}</span>}
                  </button>
                ))}
                <span className="ms-auto fs-8 text-gray-500">{totalResults ? `${totalResults} kayıt` : ''}</span>
              </div>

              <div className="notif-sheet-body" data-tour="notif-list">
                {loading && items.length === 0 && (
                  <div className="notif-skeleton-wrap">
                    {[0, 1, 2, 3].map((i) => (
                      <div key={i} className="notif-skeleton" />
                    ))}
                  </div>
                )}

                {!loading && items.length === 0 && (
                  <div className="notif-empty">
                    <Svg d={ICON.bellOff} size={40} />
                    <div className="fw-bold text-gray-700 mt-3">{filter === 'unread' ? 'Okunmamış bildiriminiz yok' : 'Henüz bildirim yok'}</div>
                    <div className="fs-8 text-gray-500">Onay kararları, mesajlar ve duyurular burada görünür.</div>
                  </div>
                )}

                <AnimatePresence initial={false}>
                  {items.map((n, idx) => (
                    <motion.div
                      key={n.id}
                      layout
                      data-tour={idx === 0 ? 'notif-item' : undefined}
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                      transition={{ duration: 0.18 }}
                      className={`notif-item ${n.is_read ? 'read' : 'unread'}`}
                      onClick={() => !n.is_read && toggleRead(n)}
                    >
                      <span className={`notif-dot ${n.user_id == null ? 'general' : ''}`} aria-hidden="true" />
                      <div className="notif-item-main">
                        <div className="d-flex align-items-start justify-content-between gap-2">
                          <div className="notif-item-title">{n.title}</div>
                          <span className="notif-item-time" title={moment(n.created_at).format('DD.MM.YYYY HH:mm')}>
                            {moment(n.created_at).locale('tr').fromNow()}
                          </span>
                        </div>
                        {n.content && <div className="notif-item-content">{n.content}</div>}
                        <div className="notif-item-meta">
                          <span className={`badge ${n.user_id == null ? 'badge-light-info' : 'badge-light-primary'} fs-9`}>{n.user_id == null ? 'Genel' : 'Bana özel'}</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        className={`btn btn-sm btn-icon notif-item-action ${n.is_read ? 'btn-light' : 'btn-light-success'}`}
                        title={n.is_read ? 'Okunmadı olarak işaretle' : 'Okundu olarak işaretle'}
                        disabled={busyIds.has(n.id)}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRead(n);
                        }}
                      >
                        <Svg d={n.is_read ? ICON.mail : ICON.mailOpen} size={15} />
                      </button>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {items.length > 0 && page < totalPages && (
                  <div className="text-center py-4">
                    <button type="button" className="d-flex flex-row center btn btn-sm btn-light" onClick={() => fetchPage(page + 1, filter, false)} disabled={loading}>
                      {loading ? <span className="spinner-border spinner-border-sm me-2" /> : <Svg d={ICON.more} size={14} className="me-2" />}
                      Daha fazla yükle
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default NotificationDock;
